/**
 * Token Storage - In-Memory JWT Token Management with sessionStorage fallback.
 *
 * Primary store: in-memory (fast, cleared on tab close).
 * Fallback: sessionStorage (survives same-tab page refreshes + in-memory clears
 * caused by the Axios 401 interceptor's aggressive clearTokens() path).
 *
 * Security: sessionStorage is tab-scoped and cleared when the tab closes.
 * It is accessible to JS (XSS risk), which is acceptable given the app already
 * stores Cognito JWTs in the browser for API calls.
 */

const SS_ID    = 'sp_id_token';
const SS_ACC   = 'sp_access_token';
const SS_REF   = 'sp_refresh_token';
const SS_EXP   = 'sp_expires_in';

interface Tokens {
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  expiresIn: number | null;
}

class TokenStorage {
  private tokens: Tokens = {
    accessToken: null,
    refreshToken: null,
    idToken: null,
    expiresIn: null,
  };

  private ss(key: string): string | null {
    try { return sessionStorage.getItem(key); } catch { return null; }
  }

  private ssSave(key: string, value: string): void {
    try { sessionStorage.setItem(key, value); } catch { /* unavailable */ }
  }

  private ssRemove(key: string): void {
    try { sessionStorage.removeItem(key); } catch { /* unavailable */ }
  }

  /**
   * Store tokens after login/refresh — persists to sessionStorage for
   * same-tab resilience against in-memory clears.
   */
  setTokens(tokens: {
    accessToken: string;
    refreshToken: string;
    idToken: string;
    expiresIn: number;
  }): void {
    this.tokens = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      idToken: tokens.idToken,
      expiresIn: tokens.expiresIn,
    };
    this.ssSave(SS_ID,  tokens.idToken);
    this.ssSave(SS_ACC, tokens.accessToken);
    this.ssSave(SS_REF, tokens.refreshToken);
    this.ssSave(SS_EXP, String(tokens.expiresIn));
  }

  /** Get access token — falls back to sessionStorage if in-memory is null */
  getAccessToken(): string | null {
    return this.tokens.accessToken ?? this.ss(SS_ACC);
  }

  /** Get refresh token — falls back to sessionStorage if in-memory is null */
  getRefreshToken(): string | null {
    return this.tokens.refreshToken ?? this.ss(SS_REF);
  }

  /** Get ID token — falls back to sessionStorage if in-memory is null */
  getIdToken(): string | null {
    // in-memory > sessionStorage > localStorage fallback (sp_ext_id_token set by external-api.ts saveTokens)
    return this.tokens.idToken ?? this.ss(SS_ID) ?? localStorage.getItem('sp_ext_id_token');
  }

  hasTokens(): boolean {
    return !!(this.getAccessToken() && this.getRefreshToken());
  }

  /**
   * Clear all tokens — wipes both in-memory and sessionStorage.
   * Called on logout or when token refresh fails unrecoverably.
   */
  clearTokens(): void {
    this.tokens = {
      accessToken: null,
      refreshToken: null,
      idToken: null,
      expiresIn: null,
    };
    this.ssRemove(SS_ID);
    this.ssRemove(SS_ACC);
    this.ssRemove(SS_REF);
    this.ssRemove(SS_EXP);
  }

  getAllTokens(): Tokens {
    return { ...this.tokens };
  }
}

// Singleton instance
const tokenStorage = new TokenStorage();

export default tokenStorage;
export { TokenStorage };
